import React, { useEffect, useState } from 'react';
import { ButtonStandard, Text } from '@stashinvest/react-components';
import useTrack from 'stash/hooks/useTrack';
import { bucketNotifications, filterByTab } from './bucketing';
import { trackNotificationCenterViewed } from './events';
import { NotificationItemCard } from './NotificationItemCard';
import type { NotificationCenterTab } from './types';
import { useContentCards } from './useContentCards';

const TABS: { id: NotificationCenterTab; label: string }[] = [
	{ id: 'all', label: 'All' },
	{ id: 'highlights', label: 'Highlights' },
	{ id: 'account', label: 'Account' },
];

function LoadingSkeleton() {
	return (
		<div className="flex flex-col gap-contentSmall" aria-busy="true" aria-label="Loading">
			{Array.from({ length: 5 }).map((_, index) => (
				<div
					// eslint-disable-next-line react/no-array-index-key
					key={index}
					className="h-20 w-full animate-pulse rounded-xl bg-surfaceContainerHigh"
				/>
			))}
		</div>
	);
}

function EmptyState() {
	return (
		<div className="mt-layoutLarge flex flex-col items-center px-contentMedium text-center">
			<Text tag="h2" textStyle="headerSmall" className="mb-content2XS">
				We have no updates
			</Text>
			<Text tag="p" textStyle="bodyMediumStandard" color="text-onSurfaceVariant">
				Please check again later.
			</Text>
		</div>
	);
}

type ErrorStateProps = {
	onRetry: () => void;
};

function ErrorState({ onRetry }: ErrorStateProps) {
	return (
		<div className="mt-layoutLarge flex flex-col items-center px-contentMedium text-center">
			<Text tag="h2" textStyle="headerSmall" className="mb-content2XS">
				Something went wrong
			</Text>
			<Text
				tag="p"
				textStyle="bodyMediumStandard"
				color="text-onSurfaceVariant"
				className="mb-contentSmall"
			>
				We could not load your notifications.
			</Text>
			<ButtonStandard onClick={onRetry}>Try again</ButtonStandard>
		</div>
	);
}

export function NotificationCenterScreen() {
	const track = useTrack();
	const [tab, setTab] = useState<NotificationCenterTab>('all');
	const { items, isLoading, hasError, hasLoaded, retry } = useContentCards(true);

	useEffect(() => {
		trackNotificationCenterViewed(track, tab);
	}, [tab, track]);

	const filtered = filterByTab(items, tab);
	const buckets = bucketNotifications(filtered);

	return (
		<div>
			<Text tag="h1" textStyle="headerMedium" className="mb-contentMedium">
				Notifications
			</Text>

			<div
				role="tablist"
				aria-label="Notification categories"
				className="mb-contentMedium flex gap-contentSmall border-b border-outlineVariant"
			>
				{TABS.map(({ id, label }) => {
					const isActive = tab === id;
					return (
						<button
							key={id}
							type="button"
							role="tab"
							aria-selected={isActive}
							onClick={() => setTab(id)}
							className={`border-0 border-b-2 bg-transparent px-0 py-content2XS ${
								isActive
									? 'border-primary text-primary'
									: 'border-transparent text-onSurfaceVariant'
							}`}
						>
							<Text
								tag="span"
								textStyle="bodyMediumEmphasize"
								color={isActive ? 'text-primary' : 'text-onSurfaceVariant'}
							>
								{label}
							</Text>
						</button>
					);
				})}
			</div>

			{isLoading && !hasLoaded ? <LoadingSkeleton /> : null}
			{hasError ? <ErrorState onRetry={retry} /> : null}
			{!isLoading && !hasError && hasLoaded && filtered.length === 0 ? (
				<EmptyState />
			) : null}

			{!hasError && filtered.length > 0 ? (
				<div className="flex flex-col gap-contentMedium">
					{buckets.map((bucket) => (
						<section
							key={bucket.type === 'spotlight' ? 'spotlight' : bucket.dayKey}
						>
							{bucket.type === 'day' ? (
								<Text
									tag="h2"
									textStyle="bodySmallEmphasize"
									color="text-onSurfaceVariant"
									className="mb-content2XS"
								>
									{bucket.label}
								</Text>
							) : null}
							<div className="flex flex-col gap-contentSmall">
								{bucket.items.map((item) => (
									<NotificationItemCard key={item.id} item={item} />
								))}
							</div>
						</section>
					))}
				</div>
			) : null}
		</div>
	);
}

export default NotificationCenterScreen;
